import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CaptionBuilder from '../CaptionBuilder';
import styles from './IptcPanel.module.css';

function SortablePill({ id, label, softDeleted, manual, isPrimary, isDragActive, onTogglePrimary }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: softDeleted });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : isDragActive && !isDragging ? 0.85 : 1,
  };

  // Click → toggle primary, but only when this pill itself wasn't dragged.
  // dnd-kit's PointerSensor activationConstraint (distance: 4 px) keeps real
  // clicks separate from drags, so this lands on click only when isDragging
  // is already false for this pill.
  const handleClick = (e) => {
    if (softDeleted || isDragging) return;
    if (typeof onTogglePrimary !== 'function') return;
    e.stopPropagation();
    onTogglePrimary(id);
  };

  return (
    <span
      ref={setNodeRef}
      style={style}
      className={clsx(
        styles.pill,
        softDeleted ? styles.pillSoftDeleted : manual && styles.pillManual,
        !softDeleted && styles.pillDraggable,
        isPrimary && styles.pillPrimary,
      )}
      onClick={handleClick}
      title={softDeleted ? undefined : isPrimary ? 'Click to clear primary subject' : 'Click to mark as primary subject'}
      {...(softDeleted ? {} : attributes)}
      {...(softDeleted ? {} : listeners)}
    >
      {label}
    </span>
  );
}

const DEBOUNCE_MS = 1500;

/**
 * Inner component that manages caption editing state.
 * Remounted via key={imageId} when the photo changes, which
 * naturally resets all local state without ref tricks.
 */
function IptcPanelInner({
  imageId,
  matchId,
  persons,
  captionOverride,
  captionChains,
  renderedCaption,
  orderedNames,
  primarySubjectPersonId,
  capturedAt,
  matchPhase,
  photographer,
  keywords,
  onCaptionChange,
  onCaptionReset,
  onKeywordsChange,
  onReorderPersons,
  onTogglePrimary,
  disabled,
  saving,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortablePills = useMemo(
    () => (orderedNames || []).filter((n) => !n.softDeleted),
    [orderedNames],
  );
  const sortableIds = useMemo(
    () => sortablePills.map((n) => n.person_id),
    [sortablePills],
  );
  const [activePillId, setActivePillId] = useState(null);

  const handlePillsDragEnd = (event) => {
    const { active, over } = event;
    setActivePillId(null);
    if (!over || active.id === over.id) return;
    const oldIdx = sortableIds.indexOf(active.id);
    const newIdx = sortableIds.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorderPersons?.(arrayMove(sortableIds, oldIdx, newIdx));
  };
  const [saveStatus, setSaveStatus] = useState(null);
  const debounceRef = useRef(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [kwSaveStatus, setKwSaveStatus] = useState(null);

  // Determine if caption is currently overridden
  const isOverridden = Boolean(captionOverride);
  const displayCaption = captionOverride || renderedCaption || '';

  // Track local edits independently from props
  const [localEdit, setLocalEdit] = useState(null);

  // Show local edit if user is actively editing, otherwise show prop value
  const shownCaption = localEdit !== null ? localEdit : displayCaption;

  // Cleanup debounce on unmount (which also happens on photo switch via key)
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Debounced save
  const handleChange = useCallback((value) => {
    setLocalEdit(value);
    setSaveStatus('typing');

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      debounceRef.current = null;
      setSaveStatus('saving');
      try {
        await onCaptionChange(value);
        setLocalEdit(null);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } catch {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    }, DEBOUNCE_MS);
  }, [onCaptionChange]);

  const handleReset = async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setLocalEdit(null);
    setSaveStatus('saving');
    try {
      await onCaptionReset();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleAddKeyword = useCallback(async (value) => {
    const trimmed = value.trim();
    if (!trimmed || !onKeywordsChange) return;
    if (keywords.includes(trimmed)) {
      setKeywordInput('');
      return;
    }
    const newKeywords = [...keywords, trimmed];
    setKeywordInput('');
    setKwSaveStatus('saving');
    try {
      await onKeywordsChange(newKeywords);
      setKwSaveStatus('saved');
      setTimeout(() => setKwSaveStatus(null), 2000);
    } catch {
      setKwSaveStatus('error');
      setTimeout(() => setKwSaveStatus(null), 3000);
    }
  }, [keywords, onKeywordsChange]);

  const handleRemoveKeyword = useCallback(async (keyword) => {
    if (!onKeywordsChange) return;
    const newKeywords = keywords.filter((k) => k !== keyword);
    setKwSaveStatus('saving');
    try {
      await onKeywordsChange(newKeywords);
      setKwSaveStatus('saved');
      setTimeout(() => setKwSaveStatus(null), 2000);
    } catch {
      setKwSaveStatus('error');
      setTimeout(() => setKwSaveStatus(null), 3000);
    }
  }, [keywords, onKeywordsChange]);

  const handleKeywordKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddKeyword(keywordInput);
    }
  }, [keywordInput, handleAddKeyword]);

  const statusText = {
    typing: '...',
    saving: 'Saving...',
    saved: 'Saved',
    error: 'Save failed',
  };

  return (
    <div className={styles.panel}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Caption</span>
          {isOverridden && <span className={styles.badge}>overridden</span>}
          {saveStatus && (
            <span className={styles.saveStatus}>{statusText[saveStatus]}</span>
          )}
        </div>
        <textarea
          className={clsx(styles.textarea, isOverridden && styles.textareaOverridden)}
          value={shownCaption}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          rows={4}
          placeholder={renderedCaption ? 'Caption from template...' : 'No caption template configured'}
        />
        {isOverridden && (
          <button
            className={styles.resetBtn}
            onClick={handleReset}
            disabled={disabled || saving}
          >
            Reset to template
          </button>
        )}
      </div>

      <div className={styles.separator} />

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Persons in image</span>
        </div>
        <div className={styles.pills}>
          {orderedNames.length === 0 ? (
            <span className={styles.emptyText}>No persons tagged</span>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(e) => setActivePillId(e.active?.id || null)}
              onDragCancel={() => setActivePillId(null)}
              onDragEnd={handlePillsDragEnd}
            >
              <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                {orderedNames.map((n) => (
                  <SortablePill
                    key={n.person_id}
                    id={n.person_id}
                    label={n.displayName || n.label}
                    softDeleted={n.softDeleted}
                    manual={!n.face}
                    isPrimary={
                      !!primarySubjectPersonId && primarySubjectPersonId === n.person_id
                    }
                    isDragActive={!!activePillId}
                    onTogglePrimary={(personId) => {
                      onTogglePrimary?.(
                        primarySubjectPersonId === personId ? null : personId,
                      );
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <div className={styles.separator} />

      <CaptionBuilder
        imageId={imageId}
        matchId={matchId}
        persons={persons}
        orderedNames={orderedNames}
        primarySubjectPersonId={primarySubjectPersonId}
        capturedAt={capturedAt}
        matchPhase={matchPhase}
        photographer={photographer}
        captionOverride={captionOverride}
        captionChains={captionChains}
        renderedCaption={renderedCaption}
        existingKeywords={keywords}
        onCaptionChange={onCaptionChange}
        onKeywordsChange={onKeywordsChange}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * IPTC metadata panel for per-photo caption editing and person display.
 *
 * Uses key={imageId} to remount the inner component when the photo changes,
 * which cleanly resets local edit state and flushes pending debounces.
 */
export default function IptcPanel({ imageId, ...props }) {
  return <IptcPanelInner key={imageId} imageId={imageId} {...props} />;
}
